import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import type { NpiHistory, NpiNoReason } from 'types/credentialing';
import { ENPIType, EPracticeType, ECredentialingModalStep, ECredentialingFlowMode } from 'types/credentialing';
import usePayerOptions from 'hooks/credentialing/usePayerOptions';
import useUpcomingPayers from 'hooks/credentialing/useUpcomingPayers';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import useCredentialingPanels from 'hooks/credentialing/useCredentialingPanels';
import {
    getCredentialingProviderStates,
    getProviderPriorCredentialing,
    addSoloProviderPayers,
    addGroupProviderPayersActive,
    getGroupProviderPayers,
    getPayerRequirements,
    savePayerRequirements,
    saveIrsLetter,
    saveCredPayoutAccount,
} from 'api/credentialing';
import type {
    IrsLetterDto,
    PayerRequirementsDto,
    ProviderCredentialingPayoutAccountDto,
    TAddressPrefill,
    TPayerChainContext,
    TSelectedPayer,
} from 'components/CredentialingPayerSteps';
import { buildPayoutDto } from 'components/CredentialingPayoutForm';
import { W9_FIRST_PAGE_HINT } from 'components/W9UploadDesc';
import {
    EPayerStep,
    GovProgramStep,
    ServiceAddressStep,
    OptumW9Step,
    AetnaFocusStep,
    IrsLetterStep,
    MedicarePayoutStep,
    Np104Step,
    NP104_TITLE,
    NP104_SUBTITLE,
    isNp104BlockedPayer,
    firstSubmitContext,
    loadPayerChainData,
    getApplicablePayerSteps,
    getChangedPayerSteps,
    selectedStates,
    additionalW9ItemValid,
    seedAdditionalW9,
    mergeSeededW9,
} from 'components/CredentialingPayerSteps';
import ProviderStore from 'store/Provider';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { EFeatureType } from 'types/provider';
import NpiHistoryStep from './components/NpiHistoryStep';
import PayerSelectStep from './components/PayerSelectStep';
import AgreementStep from './components/AgreementStep';
import ProviderSelectStep from './components/ProviderSelectStep';
import PaymentStep from './components/PaymentStep';
import ConfirmDepositsStep from './components/ConfirmDepositsStep';
import s from './s.module.less';

interface Props {
    open: boolean;
    onClose: () => void;
    targetStep?: ECredentialingModalStep;
    flowMode?: ECredentialingFlowMode;
    targetProviderId?: number;
    // Group review can jump straight to the agreement step without walking the provider-select /
    // per-provider payer steps. Passing the target providers seeds selectedProviderIds and
    // rehydrates their saved DRAFT payers so signGroupAgreement and the agreement panel aren't empty.
    targetProviderIds?: number[];
    // Basic information 的 contactAddress，Service address 步骤只预填与其同州的州块（solo INITIAL 链用）
    addressPrefill?: TAddressPrefill;
    onEnd?: () => void;
}

const CredentialingModal = ({
    open,
    onClose,
    targetStep = ECredentialingModalStep.SELECT_NPI,
    flowMode = ECredentialingFlowMode.INITIAL,
    targetProviderId,
    targetProviderIds,
    addressPrefill,
    onEnd,
}: Props) => {
    const [getUser] = ProviderStore.useStore();
    const currentProviderId = getUser('data')?.id;
    const [credentialInfo, credentialInfoLoading] = useCredentialInfo();

    // 州来源与 group 的 ProviderPayerModal 同源：按 providerId 从 licenses 推导（payer/states），
    // 而不是 overview.states——后者不随 licenses 变化，新加执照州不会出现在 payer 选择里
    const [providerStates, setProviderStates] = useState<string[]>([]);
    useEffect(() => {
        if (!open) return undefined;
        const ids = (
            credentialInfo.providers.length
                ? credentialInfo.providers.map((p) => p.providerId)
                : [targetProviderId || currentProviderId]
        ).filter(Boolean) as number[];
        if (!ids.length) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res: any = await getCredentialingProviderStates(ids);
                const list = res?.data?.data;
                if (!cancelled && Array.isArray(list)) setProviderStates(list);
            } catch (e) {
                // ignore：拿不到州时 payer 列表按空处理
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, credentialInfo.providers, targetProviderId, currentProviderId]);

    const [upcomingPayers, upcomingPayersLoading] = useUpcomingPayers(providerStates);
    const [panelRows] = useCredentialingPanels();

    const practiceType = useMemo(() => {
        const npiValue = credentialInfo.billingNpiType?.value;
        if (npiValue === ENPIType.PRACTICE_NPI) return EPracticeType.GROUP;
        return EPracticeType.SOLO;
    }, [credentialInfo.billingNpiType]);

    const isAddFlow = flowMode === ECredentialingFlowMode.ADD;
    const isGroup = practiceType === EPracticeType.GROUP && !targetProviderId;
    const isSoloAdd = isAddFlow && !isGroup;
    const isGroupAdd = isAddFlow && isGroup;
    // Ops 给账号（group 看 owner，由 owner 提交）打开了押金减免：后端建 $0 SUCCEEDED 账单，前端去掉金额 / 加卡步
    const [providerHomeInfo] = useProviderHomeInfo();
    const feeWaived = !!providerHomeInfo?.features?.includes(EFeatureType.CREDENTIALING_FEE_WAIVED);

    const uniqueProviders = useMemo(() => {
        const seen = new Set<number>();
        return credentialInfo.providers.filter((p) => {
            if (seen.has(p.providerId)) return false;
            seen.add(p.providerId);
            return true;
        });
    }, [credentialInfo.providers]);

    const inProgressPayerIds = useMemo(() => {
        const providerId = targetProviderId || currentProviderId;
        return panelRows
            .filter((row) => !providerId || row.providerId === providerId)
            .map((row) => String(row.klarityPayerId));
    }, [panelRows, targetProviderId, currentProviderId]);

    const existingPayersByProvider = useMemo(() => {
        const map: Record<string, string[]> = {};
        panelRows.forEach((row) => {
            const key = String(row.providerId);
            if (!map[key]) map[key] = [];
            map[key].push(String(row.klarityPayerId));
        });
        return map;
    }, [panelRows]);

    const initialStep = useMemo(() => {
        if (isAddFlow) {
            if (isGroup) return ECredentialingModalStep.SELECT_GROUP_PROVIDER;
            return ECredentialingModalStep.SELECT_PAYER;
        }
        return targetStep;
    }, [isAddFlow, isGroup, targetStep]);

    const [step, setStep] = useState<ECredentialingModalStep>(ECredentialingModalStep.SELECT_NPI);

    useEffect(() => {
        if (open) {
            setStep(initialStep);
        }
    }, [open, initialStep]);

    React.useEffect(() => {
        if (targetStep && !isAddFlow) {
            setStep(targetStep);
        }
    }, [isGroup, targetProviderId, targetStep, isAddFlow]);

    const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>(() =>
        uniqueProviders.filter((p) => p.onboardingStatus === 'COMPLETED').map((p) => String(p.providerId)),
    );
    const [payerPos, setPayerPos] = useState(0);
    const [perProviderPayers, setPerProviderPayers] = useState<Record<string, string[]>>({});

    // group ADD：perProviderPayers 存的是「已有 + 本次」（供选 payer 页回显、触发链按并集计算），
    // Confirm 展示和提交只能带本次新加的 payer。已有 panel（含 WITHDRAWN / PANEL_CLOSED）一并提交会被后端
    // 当成 Resubmit 静默重开并多收 deposit；重开只走 Resubmit 入口（与 solo 的 newPayersForDeposit 同规则）
    const perProviderNewPayers = useMemo(() => {
        if (!isGroupAdd) return perProviderPayers;
        const out: Record<string, string[]> = {};
        Object.entries(perProviderPayers).forEach(([id, payers]) => {
            const existing = existingPayersByProvider[id] || [];
            out[id] = payers.filter((k) => !existing.includes(k));
        });
        return out;
    }, [isGroupAdd, perProviderPayers, existingPayersByProvider]);
    // klarityPayerId -> { name, state }, from the review deep-link's group/payers response. Lets the
    // agreement step name (and state-prefix) payers that aren't in the current state's active payerOptions.
    const [reviewPayerInfo, setReviewPayerInfo] = useState<Record<string, { name: string; state: string }>>({});

    const [npiHistory, setNpiHistory] = useState<NpiHistory>('');
    const [npiNoReason, setNpiNoReason] = useState<NpiNoReason>('');
    const [alreadyCredentialedPayers, setAlreadyCredentialedPayers] = useState<string[]>([]);
    const [npiNotSure, setNpiNotSure] = useState(false);
    const [npiOtherPayers, setNpiOtherPayers] = useState('');

    const [selectedPayers, setSelectedPayers] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            setSelectedPayers([]);
        }
    }, [open]);

    // ===== V2 payer 触发步骤链（solo INITIAL / solo ADD / group ADD：Select payers 之后按需插入）=====
    const isSoloInitial = !isAddFlow && !isGroup && !targetProviderIds?.length;
    // 已保存 + 会话内编辑的 payer-requirements 工作副本；solo 打开弹窗时水合、group ADD 按 provider
    // 进链时水合，链内每步 Continue 全量覆盖保存
    const [payerReqs, setPayerReqs] = useState<PayerRequirementsDto>({});
    const [chainSteps, setChainSteps] = useState<EPayerStep[]>([]);
    const [chainPos, setChainPos] = useState(0);
    const [chainSaving, setChainSaving] = useState(false);
    // ADD 场景按 diff 跳步的前提：已存 payer-requirements 水合成功。失败时退回全量链，
    // 否则「跳过的步骤 + 链内全量覆盖保存」会把没加载到的旧数据清空
    const [payerReqsHydrated, setPayerReqsHydrated] = useState(false);
    // IRS letter / Medicare payout 两步走各自的接口（不在 payerReqs 里）：链开始前随触发上下文一起水合
    const [irsLetter, setIrsLetter] = useState<IrsLetterDto>({});
    const [payout, setPayout] = useState<ProviderCredentialingPayoutAccountDto>({});
    const [chainGroup, setChainGroup] = useState(false);
    // Medicaid 注册问卷的上下文：实体类型（operating agreement）+ 预填依据
    const [medicaidCtx, setMedicaidCtx] = useState<{
        entityType?: string;
        licenseActionNo: boolean;
        payoutOnFile: boolean;
    }>({ licenseActionNo: false, payoutOnFile: false });
    // 最近一次算链用的上下文：104 答 No 后按新选择重算剩下的步骤
    const chainCtxRef = useRef<TPayerChainContext | undefined>(undefined);
    const loadChainContext = useCallback(
        async (providerId: number | undefined, group: boolean): Promise<TPayerChainContext> => {
            const data = await loadPayerChainData(providerId, group);
            chainCtxRef.current = data.ctx;
            setIrsLetter(data.irsLetter);
            setPayout(data.payout);
            setChainGroup(group);
            setMedicaidCtx({
                entityType: data.entityType,
                licenseActionNo: data.licenseActionNo,
                payoutOnFile: data.ctx.payoutOnFile,
            });
            return data.ctx;
        },
        [],
    );

    // 上次会话的链状态不能带进新会话（影响 CONFIRM_DEPOSITS 的 Back 与 group 的 payerReqs 归属）
    useEffect(() => {
        if (!open) return;
        setChainSteps([]);
        setChainPos(0);
        setPayerReqs({});
        setPayerReqsHydrated(false);
    }, [open]);

    useEffect(() => {
        if (!open || !(isSoloInitial || isSoloAdd)) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res: any = await getPayerRequirements(targetProviderId);
                if (cancelled || !res?.data?.data) return;
                const dto = res.data.data;
                setPayerReqs(dto);
                setPayerReqsHydrated(true);
                // additionalW9 尚无 primary：从 Verification 种一条（仅首次）；深链注入的
                // other 条目保留，种子只补 primary 位
                const seeded = await seedAdditionalW9(dto.additionalW9, targetProviderId);
                if (!cancelled && seeded) {
                    setPayerReqs((prev) => ({ ...prev, additionalW9: mergeSeededW9(prev.additionalW9, seeded) }));
                }
            } catch (e) {
                // ignore：水合失败按空数据处理，链会全量出现
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, isSoloInitial, isSoloAdd, targetProviderId]);

    const CHAIN_STEP_MODAL: Record<EPayerStep, ECredentialingModalStep> = {
        [EPayerStep.NP_104]: ECredentialingModalStep.NP_104,
        [EPayerStep.GOV_PROGRAM]: ECredentialingModalStep.GOV_PROGRAM,
        [EPayerStep.IRS_LETTER]: ECredentialingModalStep.IRS_LETTER,
        [EPayerStep.SERVICE_ADDRESS]: ECredentialingModalStep.SERVICE_ADDRESS,
        [EPayerStep.MEDICARE_PAYOUT]: ECredentialingModalStep.MEDICARE_PAYOUT,
        [EPayerStep.OPTUM_W9]: ECredentialingModalStep.OPTUM_W9,
        [EPayerStep.AETNA_FOCUS]: ECredentialingModalStep.AETNA_FOCUS,
    };

    useEffect(() => {
        if (!open || !isSoloAdd) return;
        let cancelled = false;
        (async () => {
            try {
                const res: any = await getProviderPriorCredentialing(targetProviderId);
                const dto = res?.data?.data;
                if (cancelled || !dto) return;
                if (dto.priorCredentialed && dto.priorYesPayers?.byState) {
                    const payers: string[] = [];
                    dto.priorYesPayers.byState.forEach((item: { payers: string[] }) => {
                        payers.push(...item.payers);
                    });
                    setAlreadyCredentialedPayers(payers);
                    setNpiNotSure(!!dto.priorYesPayers.notSure);
                }
            } catch (e) {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, isSoloAdd, targetProviderId]);

    // Deep-link into the agreement step (group review): seed the selected providers and rehydrate
    // each one's saved DRAFT payers, since the provider-select and payer steps were skipped.
    const targetProviderIdsKey = (targetProviderIds || []).join(',');
    useEffect(() => {
        if (!open || !targetProviderIds?.length) return undefined;
        setSelectedProviderIds(targetProviderIds.map(String));
        let cancelled = false;
        (async () => {
            try {
                const res: any = await getGroupProviderPayers(targetProviderIds);
                const rows = res?.data?.data || [];
                if (cancelled) return;
                const map: Record<string, string[]> = {};
                const info: Record<string, { name: string; state: string }> = {};
                rows.forEach(
                    (row: { providerId: number; klarityPayerId: number; payerName: string; payerState?: string }) => {
                        const key = String(row.providerId);
                        if (!map[key]) map[key] = [];
                        map[key].push(String(row.klarityPayerId));
                        info[String(row.klarityPayerId)] = { name: row.payerName, state: row.payerState || '' };
                    },
                );
                setPerProviderPayers(map);
                setReviewPayerInfo(info);
            } catch (e) {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, targetProviderIdsKey]);

    const currentProviderExistingPayers = useMemo(() => {
        if (isSoloAdd) return inProgressPayerIds;
        if (targetProviderId) {
            return existingPayersByProvider[String(targetProviderId)] || [];
        }
        if (isGroup) {
            const orderedIds = uniqueProviders
                .filter((p) => selectedProviderIds.includes(String(p.providerId)))
                .map((p) => String(p.providerId));
            const id = orderedIds[payerPos];
            return id ? existingPayersByProvider[id] || [] : [];
        }
        const allExisting = Object.values(existingPayersByProvider).flat();
        return Array.from(new Set(allExisting));
    }, [
        isSoloAdd,
        inProgressPayerIds,
        targetProviderId,
        isGroup,
        existingPayersByProvider,
        uniqueProviders,
        selectedProviderIds,
        payerPos,
    ]);

    const orderedProviderIds = useMemo(
        () =>
            uniqueProviders
                .filter((p) => selectedProviderIds.includes(String(p.providerId)))
                .map((p) => String(p.providerId)),
        [uniqueProviders, selectedProviderIds],
    );

    const currentProvider = useMemo(() => {
        if (!isGroup) return null;
        const id = orderedProviderIds[payerPos];
        return uniqueProviders.find((p) => String(p.providerId) === id) || null;
    }, [isGroup, orderedProviderIds, payerPos, uniqueProviders]);

    // #19：payer 可选性按「正在选 payer 的那个 provider」的执照算（group 循环里逐个切）
    const payerProviderId = isGroup
        ? Number(orderedProviderIds[payerPos]) || undefined
        : targetProviderId || currentProviderId;
    const [payerOptions, payerOptionsLoading, refetchPayerOptions] = usePayerOptions(
        providerStates,
        providerStates.length > 0,
        payerProviderId,
    );

    // klarityPayerId 列表 → 触发计算输入（payer 名 + 州）；剔除 already-credentialed，
    // payerOptions 里找不到的（如执照州变化后下架的 payer）跳过
    const toSelectedPayers = useCallback(
        (ids: string[]): TSelectedPayer[] =>
            Array.from(new Set(ids))
                .filter((k) => !alreadyCredentialedPayers.includes(k))
                .map((k) => payerOptions.find((p) => String(p.id) === k))
                .filter(Boolean)
                .map((p) => ({ payerName: p!.name, state: p!.state, payerType: p!.payerType })),
        [alreadyCredentialedPayers, payerOptions],
    );

    // 触发计算的输入：按「已有 panel ∪ 本次选择」的并集算（产品确认口径）——
    // ADD 场景新增 payer 可能改变整体触发条件（如新增第二个州触发 Optum W-9），已存数据会预填。
    // group 下 currentProviderExistingPayers 随 payerPos 指向当前 provider；INITIAL 时已有集合为空，等价于仅本次选择
    const mapSelectedPayers = useCallback(
        (): TSelectedPayer[] => toSelectedPayers([...selectedPayers, ...currentProviderExistingPayers]),
        [selectedPayers, currentProviderExistingPayers, toSelectedPayers],
    );

    // group 循环推进：进下一个 provider 的 Select payers（预选 已有+草稿），或全部完成后按流程去终点
    const advanceProviderLoop = useCallback(() => {
        if (payerPos < orderedProviderIds.length - 1) {
            const nextPos = payerPos + 1;
            setPayerPos(nextPos);
            const nextId = orderedProviderIds[nextPos];
            const existing = existingPayersByProvider[nextId] || [];
            const saved = perProviderPayers[nextId] || [];
            setSelectedPayers(Array.from(new Set([...existing, ...saved])));
            setStep(ECredentialingModalStep.SELECT_PAYER);
            return;
        }
        setStep(isAddFlow ? ECredentialingModalStep.CONFIRM_DEPOSITS : ECredentialingModalStep.REVIEW_AGREEMENT);
    }, [payerPos, orderedProviderIds, existingPayersByProvider, perProviderPayers, isAddFlow]);

    // 链内某步 Continue：全量保存工作副本（group 存到当前循环 provider 名下），成功后进下一步；
    // 链尾出口按流程分流——solo INITIAL → agreement；solo ADD → 确认扣款；group ADD → 推进 provider 循环
    const handleChainContinue = useCallback(async () => {
        setChainSaving(true);
        try {
            const chainProviderId = isGroup ? Number(orderedProviderIds[payerPos]) : targetProviderId;
            // 每步各存各的：IRS letter / payout 走独立接口，其余步骤全量覆盖 payer-requirements
            const current = chainSteps[chainPos];
            let res: any;
            if (current === EPayerStep.IRS_LETTER) res = await saveIrsLetter(irsLetter, chainProviderId);
            else if (current === EPayerStep.MEDICARE_PAYOUT)
                res = await saveCredPayoutAccount(buildPayoutDto(payout, {}));
            else res = await savePayerRequirements(payerReqs, chainProviderId);
            if (res?.error) {
                message.error(res.error || 'Failed to save');
                return;
            }
            let steps = chainSteps;
            // 104 答 No（10/26）：Blue Shield of California 从选择里去掉、不收押金，并重新拉列表让它显示为不可选；
            // 不回到 payer 列表，按新选择重算剩下的步骤继续往后（10/27 定）
            if (current === EPayerStep.NP_104 && payerReqs.caNp104 === false) {
                const blocked = payerOptions
                    .filter((p) => isNp104BlockedPayer({ payerName: p.name, state: p.state }))
                    .map((p) => String(p.id));
                const nextSelected = selectedPayers.filter((k) => !blocked.includes(k));
                setSelectedPayers(nextSelected);
                if (isGroup)
                    setPerProviderPayers((prev) => ({ ...prev, [orderedProviderIds[payerPos]]: nextSelected }));
                refetchPayerOptions();
                const remaining = getApplicablePayerSteps(
                    toSelectedPayers([...nextSelected, ...currentProviderExistingPayers]),
                    chainCtxRef.current ? { ...chainCtxRef.current, np104: false } : undefined,
                );
                steps = [
                    ...chainSteps.slice(0, chainPos + 1),
                    ...chainSteps.slice(chainPos + 1).filter((st) => remaining.includes(st)),
                ];
                setChainSteps(steps);
            }
            if (chainPos < steps.length - 1) {
                const nextPos = chainPos + 1;
                setChainPos(nextPos);
                setStep(CHAIN_STEP_MODAL[steps[nextPos]]);
            } else if (isGroup) {
                advanceProviderLoop();
            } else {
                setStep(
                    isAddFlow ? ECredentialingModalStep.CONFIRM_DEPOSITS : ECredentialingModalStep.REVIEW_AGREEMENT,
                );
            }
        } finally {
            setChainSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        payerReqs,
        irsLetter,
        payout,
        targetProviderId,
        isGroup,
        orderedProviderIds,
        payerPos,
        chainPos,
        chainSteps,
        isAddFlow,
        advanceProviderLoop,
        payerOptions,
        selectedPayers,
        currentProviderExistingPayers,
        alreadyCredentialedPayers,
        toSelectedPayers,
        refetchPayerOptions,
    ]);

    const handleChainBack = useCallback(() => {
        if (chainPos > 0) {
            const prevPos = chainPos - 1;
            setChainPos(prevPos);
            setStep(CHAIN_STEP_MODAL[chainSteps[prevPos]]);
        } else {
            setStep(ECredentialingModalStep.SELECT_PAYER);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chainPos, chainSteps]);

    const handleNpiContinue = useCallback(() => {
        if (npiHistory !== 'direct') {
            setAlreadyCredentialedPayers([]);
            setNpiNotSure(false);
            setNpiOtherPayers('');
        }
        setSelectedPayers((prev) => prev.filter((k) => !alreadyCredentialedPayers.includes(k)));
        if (isGroup) {
            setStep(ECredentialingModalStep.SELECT_GROUP_PROVIDER);
        } else {
            setStep(ECredentialingModalStep.SELECT_PAYER);
        }
    }, [npiHistory, alreadyCredentialedPayers, isGroup]);

    const handleProviderContinue = useCallback(() => {
        setPayerPos(0);
        const firstId = orderedProviderIds[0];
        const existing = existingPayersByProvider[firstId] || [];
        const saved = perProviderPayers[firstId] || [];
        const merged = Array.from(new Set([...existing, ...saved]));
        setSelectedPayers(merged);
        setStep(ECredentialingModalStep.SELECT_PAYER);
    }, [orderedProviderIds, perProviderPayers, existingPayersByProvider]);

    const handlePayerContinue = useCallback(async () => {
        if (isGroup) {
            const currentId = orderedProviderIds[payerPos];
            setPerProviderPayers((prev) => ({ ...prev, [currentId]: selectedPayers }));
            // group ADD：该 provider 的 payer 定稿后先走他的触发链（数据按人存），链尾
            // （handleChainContinue）再推进 provider 循环。先水合他已存的 payer-requirements：
            // 成功则按 diff 只弹有变化的步骤（新触发的步骤 / 出现新州的步骤），失败退回全量链
            if (isGroupAdd) {
                const union = mapSelectedPayers();
                const ctx = await loadChainContext(Number(currentId), true);
                const fullChain = getApplicablePayerSteps(union, ctx);
                if (fullChain.length) {
                    setPayerReqs({});
                    let hydrated: PayerRequirementsDto | null = null;
                    try {
                        const res: any = await getPayerRequirements(Number(currentId));
                        if (res?.data?.data) hydrated = res.data.data;
                    } catch (e) {
                        // ignore：水合失败按空数据处理，链全量出现
                    }
                    const applicable = hydrated
                        ? getChangedPayerSteps(toSelectedPayers(existingPayersByProvider[currentId] || []), union, ctx)
                        : fullChain;
                    let nextReqs: PayerRequirementsDto = hydrated || {};
                    // 链含 W-9 步骤且该 provider 尚无 primary：从他自己的 Verification 种一条
                    if (applicable.includes(EPayerStep.OPTUM_W9)) {
                        const seeded = await seedAdditionalW9(nextReqs.additionalW9, Number(currentId));
                        if (seeded) {
                            nextReqs = { ...nextReqs, additionalW9: mergeSeededW9(nextReqs.additionalW9, seeded) };
                        }
                    }
                    setPayerReqs(nextReqs);
                    if (applicable.length) {
                        setChainSteps(applicable);
                        setChainPos(0);
                        setStep(CHAIN_STEP_MODAL[applicable[0]]);
                        return;
                    }
                }
            }
            advanceProviderLoop();
            return;
        }
        // Solo（INITIAL 与 ADD）：按整体 payer（已有 ∪ 本次选择）计算条件步骤链
        // （Gov → Service address → Optum W-9 → Aetna Focus），已存数据预填；
        // ADD 且水合成功时按 diff 只弹有变化的步骤；链算好存 state，
        // agreement / 确认扣款的 Back 也按它回到链尾
        if (isSoloInitial || isSoloAdd) {
            const union = mapSelectedPayers();
            const loadedCtx = await loadChainContext(targetProviderId, false);
            const ctx = isSoloInitial ? firstSubmitContext(loadedCtx) : loadedCtx;
            chainCtxRef.current = ctx;
            const applicable =
                isSoloAdd && payerReqsHydrated
                    ? getChangedPayerSteps(toSelectedPayers(currentProviderExistingPayers), union, ctx)
                    : getApplicablePayerSteps(union, ctx);
            setChainSteps(applicable);
            setChainPos(0);
            if (applicable.length) {
                setStep(CHAIN_STEP_MODAL[applicable[0]]);
                return;
            }
        }
        setStep(isAddFlow ? ECredentialingModalStep.CONFIRM_DEPOSITS : ECredentialingModalStep.REVIEW_AGREEMENT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isGroup,
        isGroupAdd,
        orderedProviderIds,
        payerPos,
        selectedPayers,
        isAddFlow,
        isSoloInitial,
        isSoloAdd,
        mapSelectedPayers,
        toSelectedPayers,
        payerReqsHydrated,
        currentProviderExistingPayers,
        existingPayersByProvider,
        advanceProviderLoop,
        loadChainContext,
        targetProviderId,
    ]);

    const handlePayerBack = useCallback(() => {
        if (isGroup) {
            if (payerPos > 0) {
                const currentId = orderedProviderIds[payerPos];
                setPerProviderPayers((prev) => ({ ...prev, [currentId]: selectedPayers }));
                const prevPos = payerPos - 1;
                setPayerPos(prevPos);
                const prevId = orderedProviderIds[prevPos];
                const existing = existingPayersByProvider[prevId] || [];
                const saved = perProviderPayers[prevId] || [];
                const merged = Array.from(new Set([...existing, ...saved]));
                setSelectedPayers(merged);
                return;
            }
            setStep(ECredentialingModalStep.SELECT_GROUP_PROVIDER);
            return;
        }
        if (isSoloAdd) {
            onClose();
            return;
        }
        setStep(ECredentialingModalStep.SELECT_NPI);
    }, [
        isGroup,
        payerPos,
        orderedProviderIds,
        selectedPayers,
        perProviderPayers,
        existingPayersByProvider,
        isSoloAdd,
        onClose,
    ]);

    const handleAgreementConfirm = useCallback(() => {
        // 押金已减免：签约即提交完成，没有加卡步
        if (feeWaived) {
            message.success('Credentialing submitted successfully!');
            onEnd?.();
            onClose();
            return;
        }
        setStep(ECredentialingModalStep.ADD_PAYMENT);
    }, [feeWaived, onEnd, onClose]);

    const [confirmingDeposits, setConfirmingDeposits] = useState(false);

    const handleConfirmDeposits = useCallback(async () => {
        if (isSoloAdd) {
            const newPayerIds = selectedPayers
                .filter((k) => !inProgressPayerIds.includes(k) && !alreadyCredentialedPayers.includes(k))
                .map(Number);
            if (newPayerIds.length === 0) {
                message.warning('No new payers selected');
                return;
            }
            setConfirmingDeposits(true);
            try {
                const res = await addSoloProviderPayers(newPayerIds);
                if (res && !res.error) {
                    message.success('Payers added successfully');
                    onEnd?.();
                    onClose();
                } else {
                    message.error(res?.error || 'Failed to add payers');
                }
            } catch (e) {
                console.error(e);
                message.error('Failed to add payers');
            } finally {
                setConfirmingDeposits(false);
            }
            return;
        }
        if (isGroupAdd) {
            const reqs = orderedProviderIds
                .filter((provId) => (perProviderNewPayers[provId] || []).length > 0)
                .map((provId) => ({
                    providerId: Number(provId),
                    klarityPayerIds: (perProviderNewPayers[provId] || []).map(Number),
                }));
            if (reqs.length === 0) {
                message.warning('No new payers selected');
                return;
            }
            setConfirmingDeposits(true);
            try {
                const res = await addGroupProviderPayersActive(reqs);
                if (res && !res.error) {
                    message.success('Payers added successfully');
                    onEnd?.();
                    onClose();
                } else {
                    message.error(res?.error || 'Failed to add payers');
                }
            } catch (e) {
                console.error(e);
                message.error('Failed to add payers');
            } finally {
                setConfirmingDeposits(false);
            }
            return;
        }
        setStep(ECredentialingModalStep.ADD_PAYMENT);
    }, [
        isSoloAdd,
        isGroupAdd,
        selectedPayers,
        inProgressPayerIds,
        alreadyCredentialedPayers,
        orderedProviderIds,
        perProviderNewPayers,
        onEnd,
        onClose,
    ]);

    const handlePaymentSuccess = useCallback(() => {
        message.success(isAddFlow ? 'Deposits confirmed!' : 'Credentialing submitted successfully!');
        onEnd?.();
        onClose();
    }, [onClose, onEnd, isAddFlow]);

    // 绑卡步只能从 INITIAL 签约成功后进入（ADD 流程在 Confirm deposits 就结束了）：此时申请已提交，
    // 暂不绑卡同样按完成处理，进 submitted 页（那里会提示 Payment needed）
    const handleAddPaymentLater = useCallback(() => {
        onEnd?.();
        onClose();
    }, [onClose, onEnd]);
    const onPaymentStep = step === ECredentialingModalStep.ADD_PAYMENT;

    const allSelectedPayers = useMemo(() => {
        if (!isGroup) return selectedPayers;
        const all = new Set<string>();
        Object.values(perProviderPayers).forEach((payers) => {
            payers.forEach((p) => all.add(p));
        });
        return Array.from(all);
    }, [isGroup, selectedPayers, perProviderPayers]);

    // 押金按 panel × provider 计（与协议页 Total deposits 同口径）：group 下同一 payer 被 N 位 provider 选中算 N 份
    const panelCount = useMemo(() => {
        const payerLists = isGroup ? Object.values(perProviderPayers) : [selectedPayers];
        return payerLists.reduce(
            (sum, payers) => sum + payers.filter((k) => !alreadyCredentialedPayers.includes(k)).length,
            0,
        );
    }, [isGroup, perProviderPayers, selectedPayers, alreadyCredentialedPayers]);

    const selectedProviderCount = orderedProviderIds.length;

    const newPayersForDeposit = useMemo(() => {
        if (!isSoloAdd) return selectedPayers;
        return selectedPayers.filter((k) => !inProgressPayerIds.includes(k) && !alreadyCredentialedPayers.includes(k));
    }, [isSoloAdd, selectedPayers, inProgressPayerIds, alreadyCredentialedPayers]);

    // group ADD 链步骤按 provider 各走一遍：标题带上当前 provider 名，owner 知道在替谁填
    const chainProviderSuffix =
        isGroup && currentProvider
            ? ` · ${`${currentProvider.firstName || ''} ${currentProvider.lastName || ''}`.trim()}`
            : '';

    const getTitle = () => {
        switch (step) {
            case ECredentialingModalStep.SELECT_NPI:
                return 'Have you ever been credentialed under your own NPI?';
            case ECredentialingModalStep.SELECT_GROUP_PROVIDER:
                return 'Add payers';
            case ECredentialingModalStep.SELECT_PAYER:
                if (isSoloAdd || isGroup) return 'Add payers';
                return 'Select your payers';
            case ECredentialingModalStep.NP_104:
                return `${NP104_TITLE}${chainProviderSuffix}`;
            case ECredentialingModalStep.GOV_PROGRAM:
                return `Government program details${chainProviderSuffix}`;
            case ECredentialingModalStep.IRS_LETTER:
                return `IRS Tax ID confirmation letter${chainProviderSuffix}`;
            case ECredentialingModalStep.SERVICE_ADDRESS:
                return `Service address by state${chainProviderSuffix}`;
            case ECredentialingModalStep.MEDICARE_PAYOUT:
                return `Payout account${chainProviderSuffix}`;
            case ECredentialingModalStep.OPTUM_W9:
                return `UnitedHealthcare: a signed W-9 for each Tax ID${chainProviderSuffix}`;
            case ECredentialingModalStep.AETNA_FOCUS:
                return `Practice focus areas${chainProviderSuffix}`;
            case ECredentialingModalStep.REVIEW_AGREEMENT:
                return 'Review and confirm';
            case ECredentialingModalStep.CONFIRM_DEPOSITS:
                if (feeWaived) return 'Confirm payers';
                return isSoloAdd ? 'Confirm deposits' : 'Confirm deposits';
            case ECredentialingModalStep.ADD_PAYMENT:
                return 'Add payment information';
            default:
                return '';
        }
    };

    const getSubtitle = () => {
        switch (step) {
            case ECredentialingModalStep.SELECT_NPI:
                return 'This helps us tailor your payer options.';
            case ECredentialingModalStep.SELECT_GROUP_PROVIDER:
                return "First, choose which providers you're adding payers for. You'll pick the payers for them next.";
            case ECredentialingModalStep.SELECT_PAYER:
                if (feeWaived) {
                    return isAddFlow
                        ? "Select the payers to add. Your credentialing fee is waived by Klarity, so there's no deposit to pay."
                        : "Choose the payers you want to be credentialed with. Your credentialing fee is waived by Klarity, so there's no deposit to pay.";
                }
                if (isSoloAdd)
                    return 'Select additional payers to credential. Each panel is a $50 refundable deposit, returned as cashback after your first claim.';
                return isGroup
                    ? 'Select the payers to add. Each panel is a $50 refundable deposit per provider, returned as cashback after the first claim.'
                    : 'Choose the payers you want to be credentialed with. Credentialing is free. Each panel has a $50 deposit, and the first 3 deposits are returned as cashback after your first insurance claim is submitted to Klarity.';
            case ECredentialingModalStep.NP_104:
                return NP104_SUBTITLE;
            case ECredentialingModalStep.GOV_PROGRAM:
                return "Both are confirmed separately for each state you're credentialing in. Medicare issues its certification number through a regional contractor, and Medicaid is run by each state. Payments always go to your own practice account, with Klarity listed as your billing agent, not the payee.";
            case ECredentialingModalStep.IRS_LETTER:
                return 'Medicare, Medicaid, Molina and the Blue plans verify the Tax ID you bill under against the IRS record, and a W-9 does not count because you fill that one in yourself.';
            case ECredentialingModalStep.MEDICARE_PAYOUT:
                return 'Add the bank account where your insurance reimbursements will be deposited once you are credentialed. Payers pay this account directly, and your bank details are encrypted and never shared.';
            case ECredentialingModalStep.SERVICE_ADDRESS:
                return "Payers require an address of record in every state you're applying to, including virtual visit only, used for contractual and statutory notices, not published as a place of care. We've started each one with your practice address, which you can edit if you have a different one on file.";
            case ECredentialingModalStep.OPTUM_W9: {
                // primary（Verification 种子）完整才提"已经收过一份"；group provider 未填 Verification 时换中性说法
                const primaryW9 = (payerReqs.additionalW9?.items || []).find((it) => it.primary);
                return `${
                    additionalW9ItemValid(primaryW9)
                        ? 'UnitedHealthcare needs a signed W-9 for every Tax ID and business name (DBA) you have billed claims under, with no limit on how many. We already have one from your Verification step, so most practices just confirm it here.'
                        : 'UnitedHealthcare needs a signed W-9 for every Tax ID and business name (DBA) you have billed claims under, with no limit on how many.'
                } ${W9_FIRST_PAGE_HINT}`;
            }
            case ECredentialingModalStep.AETNA_FOCUS:
                return 'Aetna asks for up to 8 focus areas that best describe your practice.';
            case ECredentialingModalStep.REVIEW_AGREEMENT:
                if (feeWaived) {
                    return isGroup
                        ? `Review your selected payers and agree to the credentialing terms for your ${selectedProviderCount} providers. Your credentialing fee is waived by Klarity, so there's no deposit to pay.`
                        : "Review your selected payers and agree to the credentialing terms. Your credentialing fee is waived by Klarity, so there's no deposit to pay.";
                }
                return isGroup
                    ? `Review your selected payers and agree to the credentialing terms for your ${selectedProviderCount} providers. Credentialing is free. The $50 deposit per panel is fully refundable, returned to you as cashback after your first claim.`
                    : 'Review your selected payers and agree to the credentialing terms. Credentialing is free. The $50 deposit per panel is fully refundable, returned to you as cashback after your first claim.';
            case ECredentialingModalStep.CONFIRM_DEPOSITS:
                if (feeWaived) {
                    return isGroup
                        ? "Review each provider's additional payers. Your credentialing fee is waived by Klarity."
                        : 'Review your additional payer selection. Your credentialing fee is waived by Klarity.';
                }
                if (isSoloAdd)
                    return 'Review your additional payer selection. Your existing credentialing information will be reused.';
                return isGroup
                    ? `Review each provider's additional payers. The deposit is $50 per panel, one panel per provider.`
                    : 'Review your additional payer selection';
            case ECredentialingModalStep.ADD_PAYMENT:
                return '';
            default:
                return '';
        }
    };

    const payerBackFn = (() => {
        if (isSoloAdd) return undefined;
        return handlePayerBack;
    })();

    return (
        <Modal
            open={open}
            title={null}
            footer={null}
            // 绑卡步去掉 X 并禁用遮罩 / ESC 关闭，唯一出口是 Add payment later 或绑卡成功；
            // onCancel 仍兜底按完成处理，避免停在已签约的 review 页
            onCancel={onPaymentStep ? handleAddPaymentLater : onClose}
            destroyOnClose
            width={960}
            className={s.credModal}
            closable={!onPaymentStep}
            maskClosable={!onPaymentStep}
            keyboard={!onPaymentStep}
        >
            <div className={s.modalHeader}>
                <h3 className={s.modalTitle}>{getTitle()}</h3>
                <p className={s.modalSubtitle}>{getSubtitle()}</p>
            </div>

            {step === ECredentialingModalStep.SELECT_NPI && (
                <NpiHistoryStep
                    npiHistory={npiHistory}
                    npiNoReason={npiNoReason}
                    alreadyCredentialedPayers={alreadyCredentialedPayers}
                    npiNotSure={npiNotSure}
                    npiOtherPayers={npiOtherPayers}
                    onNpiHistoryChange={setNpiHistory}
                    onNpiNoReasonChange={setNpiNoReason}
                    onAlreadyCredentialedChange={setAlreadyCredentialedPayers}
                    onNpiNotSureChange={setNpiNotSure}
                    onNpiOtherPayersChange={setNpiOtherPayers}
                    onContinue={handleNpiContinue}
                    payerOptions={payerOptions}
                    providerStates={providerStates}
                    providerId={targetProviderId || currentProviderId}
                />
            )}

            {step === ECredentialingModalStep.SELECT_GROUP_PROVIDER && (
                <ProviderSelectStep
                    providers={uniqueProviders}
                    selectedProviderIds={selectedProviderIds}
                    onSelectedProviderIdsChange={setSelectedProviderIds}
                    onContinue={handleProviderContinue}
                />
            )}

            {step === ECredentialingModalStep.SELECT_PAYER && (
                <PayerSelectStep
                    feeWaived={feeWaived}
                    selectedPayers={selectedPayers}
                    alreadyCredentialedPayers={alreadyCredentialedPayers}
                    existingPayers={currentProviderExistingPayers}
                    npiNotSure={npiNotSure}
                    onSelectedPayersChange={setSelectedPayers}
                    onBack={payerBackFn}
                    onContinue={handlePayerContinue}
                    currentProvider={currentProvider}
                    providerPos={isGroup ? payerPos : undefined}
                    providerTotal={isGroup ? orderedProviderIds.length : undefined}
                    payerOptions={payerOptions}
                    upcomingPayers={upcomingPayers}
                    payerOptionsLoading={payerOptionsLoading || upcomingPayersLoading || credentialInfoLoading}
                    providerStates={providerStates}
                />
            )}

            {step === ECredentialingModalStep.GOV_PROGRAM && (
                <GovProgramStep
                    states={selectedStates(mapSelectedPayers())}
                    medicare={payerReqs.medicare || []}
                    medicaid={payerReqs.medicaid || []}
                    onChange={(medicare, medicaid) => setPayerReqs((prev) => ({ ...prev, medicare, medicaid }))}
                    management={payerReqs.medicareManagement}
                    onManagementChange={(medicareManagement) =>
                        setPayerReqs((prev) => ({ ...prev, medicareManagement }))
                    }
                    medicaidEnrollment={payerReqs.medicaidEnrollment}
                    onMedicaidEnrollmentChange={(medicaidEnrollment) =>
                        setPayerReqs((prev) => ({ ...prev, medicaidEnrollment }))
                    }
                    // 2 个以上 provider 的 practice：practice 已在 Set up your practice 答过 business 题，
                    // 这里只问个人那几题（原型 mcdPath）
                    medicaidVariant={chainGroup && uniqueProviders.length > 1 ? 'rendering' : 'individual'}
                    entityType={medicaidCtx.entityType}
                    licenseActionNo={medicaidCtx.licenseActionNo}
                    payoutOnFile={medicaidCtx.payoutOnFile}
                    onContinue={handleChainContinue}
                    onBack={handleChainBack}
                    saving={chainSaving}
                />
            )}

            {step === ECredentialingModalStep.NP_104 && (
                <Np104Step
                    value={payerReqs.caNp104}
                    onChange={(caNp104) => setPayerReqs((prev) => ({ ...prev, caNp104 }))}
                    onContinue={handleChainContinue}
                    onBack={handleChainBack}
                    saving={chainSaving}
                />
            )}

            {step === ECredentialingModalStep.IRS_LETTER && (
                <IrsLetterStep
                    value={irsLetter}
                    onChange={setIrsLetter}
                    onContinue={handleChainContinue}
                    onBack={handleChainBack}
                    saving={chainSaving}
                />
            )}

            {step === ECredentialingModalStep.MEDICARE_PAYOUT && (
                <MedicarePayoutStep
                    group={chainGroup}
                    value={payout}
                    onChange={setPayout}
                    onContinue={handleChainContinue}
                    onBack={handleChainBack}
                    saving={chainSaving}
                />
            )}

            {step === ECredentialingModalStep.SERVICE_ADDRESS && (
                <ServiceAddressStep
                    states={selectedStates(mapSelectedPayers())}
                    value={payerReqs.serviceAddresses || []}
                    onChange={(serviceAddresses) => setPayerReqs((prev) => ({ ...prev, serviceAddresses }))}
                    onContinue={handleChainContinue}
                    onBack={handleChainBack}
                    saving={chainSaving}
                    prefill={addressPrefill}
                />
            )}

            {step === ECredentialingModalStep.OPTUM_W9 && (
                <OptumW9Step
                    value={payerReqs.additionalW9 || {}}
                    onChange={(additionalW9) => setPayerReqs((prev) => ({ ...prev, additionalW9 }))}
                    onContinue={handleChainContinue}
                    onBack={handleChainBack}
                    saving={chainSaving}
                />
            )}

            {step === ECredentialingModalStep.AETNA_FOCUS && (
                <AetnaFocusStep
                    value={payerReqs.focusAreas || []}
                    onChange={(focusAreas) => setPayerReqs((prev) => ({ ...prev, focusAreas }))}
                    onContinue={handleChainContinue}
                    onBack={handleChainBack}
                    saving={chainSaving}
                />
            )}

            {step === ECredentialingModalStep.REVIEW_AGREEMENT && (
                <AgreementStep
                    feeWaived={feeWaived}
                    selectedPayers={allSelectedPayers}
                    alreadyCredentialedPayers={alreadyCredentialedPayers}
                    isGroup={isGroup}
                    isAddFlow={isAddFlow}
                    providerIds={targetProviderId ? [targetProviderId] : orderedProviderIds.map(Number)}
                    perProviderPayers={perProviderPayers}
                    providers={uniqueProviders}
                    payerOptions={payerOptions}
                    payerInfoFallback={reviewPayerInfo}
                    onBack={
                        // Review deep-link jumps straight here with no payer step behind it, so
                        // Back closes the modal instead of navigating to an unreachable step.
                        // Solo INITIAL 走过条件步骤链时，Back 回到链尾而不是 Select payers。
                        targetProviderIds?.length
                            ? onClose
                            : () => {
                                  if (isSoloInitial && chainSteps.length) {
                                      const lastPos = chainSteps.length - 1;
                                      setChainPos(lastPos);
                                      setStep(CHAIN_STEP_MODAL[chainSteps[lastPos]]);
                                      return;
                                  }
                                  setStep(ECredentialingModalStep.SELECT_PAYER);
                              }
                    }
                    onConfirm={handleAgreementConfirm}
                />
            )}

            {step === ECredentialingModalStep.CONFIRM_DEPOSITS && (
                <ConfirmDepositsStep
                    feeWaived={feeWaived}
                    isGroup={isGroup}
                    perProviderPayers={perProviderNewPayers}
                    providers={uniqueProviders}
                    selectedPayers={isSoloAdd ? newPayersForDeposit : allSelectedPayers}
                    payerOptions={payerOptions}
                    onConfirm={handleConfirmDeposits}
                    onBack={() => {
                        if (isGroup) {
                            // 回到最后一个 provider 的 Select payers；再 Continue 会重新进他的链（预填）
                            const lastPos = orderedProviderIds.length - 1;
                            setPayerPos(lastPos);
                            const lastId = orderedProviderIds[lastPos];
                            const existing = existingPayersByProvider[lastId] || [];
                            const saved = perProviderPayers[lastId] || [];
                            setSelectedPayers(Array.from(new Set([...existing, ...saved])));
                            setStep(ECredentialingModalStep.SELECT_PAYER);
                            return;
                        }
                        // solo ADD 走过链时 Back 回链尾，与 agreement 的 Back 同规则
                        if (chainSteps.length) {
                            const lastPos = chainSteps.length - 1;
                            setChainPos(lastPos);
                            setStep(CHAIN_STEP_MODAL[chainSteps[lastPos]]);
                            return;
                        }
                        setStep(ECredentialingModalStep.SELECT_PAYER);
                    }}
                    confirmLoading={confirmingDeposits}
                />
            )}

            {step === ECredentialingModalStep.ADD_PAYMENT && (
                <PaymentStep
                    panelCount={panelCount}
                    isGroup={isGroup}
                    providerCount={selectedProviderCount}
                    onAddLater={handleAddPaymentLater}
                    onSuccess={handlePaymentSuccess}
                    hidePaymentChangeMessageTips
                />
            )}
        </Modal>
    );
};

export default CredentialingModal;
